import React, { useEffect, useMemo, useState } from "react";
import DoctorHeader from "./DoctorHeader";
import DoctorSidebar, { DoctorSidebarDrawer } from "./DoctorSidebar";
import DoctorFooter from "./DoctorFooter";
import { IconButton, Button, CircularProgress } from "@mui/material";
import {
  Menu,
  RefreshCw,
  UserRound,
  Hospital,
  MapPin,
  Phone,
  ExternalLink,
  Clock3,
  CheckCircle2,
  XCircle,
  ClipboardList,
} from "lucide-react";
import axios from "axios";
import SimpleSnackbar from "../Snackbar";
import hospitalsList from "../../hospitals";

const AdminReferralsPage = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(false);

  const [snackMessage, setSnackMessage] = useState("");
  const [snackColor, setSnackColor] = useState("success");
  const [snackLoading, setSnackLoading] = useState(false);

  const fetchReferrals = async () => {
    try {
      setLoading(true);
      setSnackMessage("Loading referrals...");
      setSnackColor("info");
      setSnackLoading(true);

      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/referrals`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setReferrals(res.data.referrals || []);

      setSnackMessage("Referrals loaded");
      setSnackColor("success");
    } catch (error) {
      console.error(error);
      setSnackMessage("Failed to load referrals");
      setSnackColor("error");
    } finally {
      setLoading(false);
      setSnackLoading(false);
    }
  };

  useEffect(() => {
    fetchReferrals();
  }, []);

  const closeReferral = async (id) => {
    try {
      setLoading(true);
      setSnackMessage("Closing referral...");
      setSnackColor("info");
      setSnackLoading(true);

      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/referrals/${id}/close`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setReferrals((prev) =>
        prev.map((referral) =>
          referral._id === id ? { ...referral, status: "closed" } : referral
        )
      );

      setSnackMessage("Referral closed");
      setSnackColor("success");
    } catch (error) {
      console.error(error);
      setSnackMessage("Failed to close referral");
      setSnackColor("error");
    } finally {
      setLoading(false);
      setSnackLoading(false);
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "accepted":
        return "bg-emerald-50 text-emerald-700";
      case "rejected":
        return "bg-red-50 text-red-700";
      case "closed":
        return "bg-slate-100 text-slate-600";
      case "pending":
      default:
        return "bg-amber-50 text-amber-700";
    }
  };

  const stats = useMemo(() => {
    const total = referrals.length;
    const pending = referrals.filter((r) => r.status === "pending").length;
    const accepted = referrals.filter((r) => r.status === "accepted").length;
    const rejected = referrals.filter((r) => r.status === "rejected").length;
    const closed = referrals.filter((r) => r.status === "closed").length;

    return {
      total,
      pending,
      accepted,
      rejected,
      closed,
    };
  }, [referrals]);

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <DoctorHeader />

      {/* Mobile Header */}
      <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 md:hidden">
        <div>
          <p className="text-xs text-slate-400">Admin</p>
          <h1 className="text-base font-semibold text-slate-950">
            Referrals
          </h1>
        </div>

        <IconButton onClick={() => setDrawerOpen(true)}>
          <Menu />
        </IconButton>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <div className="hidden lg:flex">
          <DoctorSidebar activeTab="Referrals" setActiveTab={() => {}} />
        </div>

        <DoctorSidebarDrawer
          open={drawerOpen}
          onOpen={() => setDrawerOpen(true)}
          onClose={() => setDrawerOpen(false)}
          activeTab="Referrals"
          setActiveTab={() => setDrawerOpen(false)}
        />

        {/* Main */}
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            {/* Page Title */}
            <div className="mb-8 flex flex-col gap-5">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h2 className="text-2xl font-semibold tracking-[-0.03em] text-slate-950 sm:text-3xl">
                    Referrals
                  </h2>

                  <p className="mt-2 text-sm text-slate-500">
                    Track hospital referrals and close completed requests.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={fetchReferrals}
                  className="inline-flex h-11 w-fit items-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-medium text-white transition hover:bg-slate-800"
                >
                  {loading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <RefreshCw size={16} />
                  )}
                  Refresh
                </button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
                <StatCard
                  title="Total referrals"
                  value={stats.total.toLocaleString()}
                  icon={ClipboardList}
                />

                <StatCard
                  title="Pending"
                  value={stats.pending.toLocaleString()}
                  icon={Clock3}
                />

                <StatCard
                  title="Accepted"
                  value={stats.accepted.toLocaleString()}
                  icon={CheckCircle2}
                />

                <StatCard
                  title="Rejected"
                  value={stats.rejected.toLocaleString()}
                  icon={XCircle}
                />

                <StatCard
                  title="Closed"
                  value={stats.closed.toLocaleString()}
                  icon={CheckCircle2}
                />
              </div>
            </div>

            {/* List Header */}
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-950">
                Referral list
              </h3>

              <p className="text-sm text-slate-500">
                {referrals.length.toLocaleString()} referral
                {referrals.length === 1 ? "" : "s"}
              </p>
            </div>

            {/* Empty */}
            {!loading && referrals.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                  <Hospital size={20} className="text-slate-500" />
                </div>

                <h3 className="text-base font-semibold text-slate-950">
                  No referrals found
                </h3>

                <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-slate-500">
                  Patient hospital referrals will appear here.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {referrals.map((referral) => (
                  <ReferralCard
                    key={referral._id}
                    referral={referral}
                    closeReferral={closeReferral}
                    getStatusStyle={getStatusStyle}
                  />
                ))}
              </div>
            )}
          </div>
        </main>
      </div>


      <SimpleSnackbar
        message={snackMessage}
        color={snackColor}
        isloading={snackLoading}
        onClose={() => setSnackMessage("")}
      />
    </div>
  );
};

const StatCard = ({ title, value, icon: Icon }) => {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm text-slate-500">{title}</p>

          <p className="mt-2 text-2xl font-semibold tracking-[-0.03em] text-slate-950">
            {value}
          </p>
        </div>

        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
          <Icon size={18} />
        </div>
      </div>
    </div>
  );
};

const ReferralCard = ({ referral, closeReferral, getStatusStyle }) => {
  const hospital = hospitalsList.find((item) => item.id == referral.hospitalId);

  const patientName = referral.patientId?.name || "Unknown Patient";
  const diagnosis = referral.diagnosis || "No diagnosis";
  const status = referral.status || "pending";

  const mapLink = hospital
    ? `https://www.google.com/maps?q=${hospital.location.lat},${hospital.location.lng}`
    : null;

  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-5 transition hover:border-slate-300 hover:shadow-sm">
      {/* Top */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-slate-900 text-sm font-semibold text-white">
            {patientName
              .split(" ")
              .filter(Boolean)
              .map((item) => item[0])
              .join("")
              .slice(0, 2)
              .toUpperCase() || "PT"}
          </div>

          <div className="min-w-0">
            <h4 className="truncate text-sm font-semibold text-slate-950">
              {patientName}
            </h4>

            <p className="mt-1 line-clamp-2 text-xs leading-5 text-slate-500">
              {diagnosis}
            </p>

            <p className="mt-1 text-xs text-slate-400">
              {new Date(referral.createdAt).toLocaleString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              })}
            </p>
          </div>
        </div>

        <span
          className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium ${getStatusStyle(
            status
          )}`}
        >
          {status}
        </span>
      </div>

      {/* Hospital */}
      <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
        <p className="mb-3 text-xs font-medium uppercase tracking-wide text-slate-400">
          Hospital
        </p>

        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-slate-600">
            <Hospital size={18} />
          </div>

          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-slate-950">
              {hospital?.name || "Unknown Hospital"}
            </p>

            <p className="mt-1 flex items-start gap-1 text-xs leading-5 text-slate-500">
              <MapPin size={13} className="mt-0.5 shrink-0" />
              <span className="line-clamp-2">
                {hospital?.address || "-"}
              </span>
            </p>

            {hospital?.phoneNumbers?.[0] && (
              <p className="mt-1 flex items-center gap-1 text-xs text-slate-500">
                <Phone size={13} />
                {hospital.phoneNumbers[0]}
              </p>
            )}

            {mapLink && (
              <a
                href={mapLink}
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-slate-700 hover:underline"
              >
                View on map
                <ExternalLink size={13} />
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Action */}
      <div className="mt-5 flex items-center justify-between">
        <p className="text-xs text-slate-400">
          Referral ID: {String(referral._id).slice(-6)}
        </p>

        {status === "pending" && (
          <Button
            size="small"
            onClick={() => closeReferral(referral._id)}
            sx={{
              minHeight: 38,
              bgcolor: "#075E54",
              color: "white",
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "none",
              px: 2.5,
              "&:hover": {
                bgcolor: "#064b43",
                boxShadow: "none",
              },
            }}
          >
            Close
          </Button>
        )}

        {status === "closed" && (
          <span className="inline-flex items-center gap-1 rounded-xl bg-slate-100 px-3 py-2 text-xs font-medium text-slate-600">
            <CheckCircle2 size={14} />
            Closed
          </span>
        )}
      </div>
    </article>
  );
};

export default AdminReferralsPage;
